import React, { useEffect, useState } from 'react';

const apiStock = '/api/stock-images';
const apiAssoc = '/api/stock-images/associate';
const apiDelete = '/api/stock-image';
const apiDownload = '/api/download-image';

const slots = ['pour-elle', 'pour-lui', 'carousel-1', 'carousel-2', 'carousel-3'];
const slotLabel = {
  'pour-elle': 'For Her',
  'pour-lui': 'For Him',
  'carousel-1': 'Hero 01',
  'carousel-2': 'Hero 02',
  'carousel-3': 'Hero 03',
};

function getPriority(img) {
  if (img.slot) {
    const idx = slots.indexOf(img.slot);
    return idx >= 0 ? idx : slots.length;
  }

  const match = /image-(\d+)\.jpg$/i.exec(img.name || img.src || '');
  return match ? slots.length + Number(match[1]) : slots.length + 999;
}

function isImagePattern(name) {
  return /^image-\d+\.jpg$/i.test(name || '');
}

function formatImageLabel(value = '') {
  return value
    .replace(/\.jpg$/i, '')
    .split('-')
    .map((part) => (part.length <= 2 ? part.toUpperCase() : part.charAt(0).toUpperCase() + part.slice(1)))
    .join(' ');
}

function getCurrentImageLabel(img) {
  const key = (img.name || '').replace(/\.jpg$/i, '');
  return slotLabel[key] || slotLabel[img.slot] || formatImageLabel(img.name || img.label);
}

export default function ImagesSelector() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [url, setUrl] = useState('');
  const [cacheSeed, setCacheSeed] = useState(Date.now());

  const load = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${apiStock}?t=${Date.now()}-${cacheSeed}`, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setImages(data.images || []);
      setError('');
      setCacheSeed(Date.now());
    } catch (e) {
      setError('Unable to load storefront images.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const delImage = async (fileName) => {
    if (!window.confirm('Delete this image?')) return;
    await fetch(`${apiDelete}/${encodeURIComponent(fileName)}`, { method: 'DELETE', cache: 'no-store' });
    await load();
  };

  const addImage = async () => {
    if (!url.trim()) return;

    try {
      const res = await fetch(`${apiDownload}?url=${encodeURIComponent(url.trim())}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Download failed');
      setUrl('');
      await load();
    } catch (e) {
      alert(`Add failed: ${e.message}`);
    }
  };

  const associateAndUpdate = async (fileName, slot) => {
    try {
      const res = await fetch(`${apiAssoc}?t=${Date.now()}-${cacheSeed}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-store' },
        body: JSON.stringify({ fileName, slot }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Association failed');
      if (data.images) {
        setImages(data.images);
        setCacheSeed(Date.now());
      }
    } catch (e) {
      alert(`Assignment failed: ${e.message}`);
    }
  };

  const sortedImages = [...images].sort((a, b) => {
    const pa = getPriority(a);
    const pb = getPriority(b);
    if (pa !== pb) return pa - pb;
    return (a.name || a.src || '').localeCompare(b.name || b.src || '');
  });

  const currentImages = sortedImages.filter((img) => !isImagePattern(img.name || img.src));
  const availableImages = sortedImages.filter((img) => isImagePattern(img.name || img.src));

  if (loading && !images.length && !error) {
    return (
      <section className="admin-view admin-view--images">
        <div className="section-card stock-loading">Loading image library...</div>
      </section>
    );
  }

  if (error && !images.length) {
    return (
      <section className="admin-view admin-view--images">
        <div className="section-card stock-error">{error}</div>
      </section>
    );
  }

  return (
    <section className="admin-view admin-view--images">
      <div className="stock-page stock-page--simple">
        <div className="stock-page__intro section-card">
          <div>
            <span className="eyebrow">Image Manager</span>
            <h1>Storefront images</h1>
            <p>
              Manage the live storefront image slots and keep your `image-#` library ready for future swaps.
            </p>
          </div>

          <div className="stock-panel">
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Remote HTTPS image URL"
            />
            <button type="button" onClick={addImage}>Add image</button>
          </div>
        </div>

        <div className="stock-section stock-section--simple section-card">
          <div className="stock-section__header stock-section__header--stacked">
            <div>
              <span className="eyebrow">Live Slots</span>
              <h2>Currently used pictures</h2>
            </div>
          </div>

          {currentImages.length > 0 ? (
            <div className="stock-grid stock-grid--used">
              {currentImages.map((img) => (
                <div className="stock-card stock-card--featured" key={img.id}>
                  <img src={`${img.src}?t=${cacheSeed}`} alt={img.name || img.label} />
                  <div className="card-body card-body--compact">
                    <div>
                      <span className="card-label">{getCurrentImageLabel(img)}</span>
                      <small className="card-note">{img.name}</small>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="stock-empty">No storefront images are currently assigned.</p>
          )}
        </div>

        <div className="stock-section stock-section--simple section-card">
          <div className="stock-section__header stock-section__header--stacked">
            <div>
              <span className="eyebrow">Library</span>
              <h2>Available image files</h2>
            </div>
          </div>

          {availableImages.length > 0 ? (
            <div className="stock-grid">
              {availableImages.map((img) => (
                <div className="stock-card" key={img.id}>
                  <img src={`${img.src}?t=${cacheSeed}`} alt={img.name || img.label} />
                  <div className="card-body">
                    <div>
                      <span className="card-label">{formatImageLabel(img.name || img.label)}</span>
                      <small className="card-note">{img.name}</small>
                    </div>
                    <button type="button" onClick={() => delImage(img.name)}>Delete</button>
                  </div>

                  <div className="stock-slot-list">
                    {slots.map((slot) => (
                      <button
                        key={slot}
                        type="button"
                        className={img.slot === slot ? 'is-active' : ''}
                        onClick={() => associateAndUpdate(img.name, slot)}
                      >
                        {slotLabel[slot]}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="stock-empty">No `image-#` files are available in the library yet.</p>
          )}
        </div>
      </div>
    </section>
  );
}
